import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtMT } from "@/lib/mountainTime";
import { cancelJob, resolveJob, errText } from "@/lib/fbLabApi";

const TONE = { published: "bg-emerald-100 text-emerald-900", done: "bg-emerald-100 text-emerald-900", failed: "bg-red-100 text-red-800", uncertain: "bg-amber-200 text-amber-950",
  cancelled: "bg-stone-200 text-stone-700", not_posted: "bg-stone-200 text-stone-700", awaiting_review: "bg-blue-100 text-blue-900", pending_approval: "bg-violet-100 text-violet-900" };
const LABEL = { queued: "Waiting for extension", claimed: "Picked up", preparing: "Preparing", uploading: "Uploading media", media_ready: "Media ready",
  awaiting_review: "Waiting for you to click Post", submitting: "Posting", published: "Published", failed: "Failed", cancelled: "Cancelled",
  uncertain: "Uncertain — check Facebook", not_posted: "Confirmed not posted", done: "Done", step: "Step", pending_approval: "Waiting for the post to appear" };

function Event({ e }) {
  return (
    <li className={e.status === "step" ? "text-stone-500" : ""}>
      {fmtMT(e.at)} · {LABEL[e.status] || e.status}{e.step ? ` [${e.step}]` : ""} — {e.detail}
      {e.diag && <details className="inline"><summary className="inline cursor-pointer text-[#1877F2] ml-1">page details</summary><pre className="whitespace-pre-wrap bg-stone-50 border rounded p-1 mt-1">{JSON.stringify(e.diag, null, 2)}</pre></details>}
    </li>
  );
}
const Candidates = ({ j, onChange }) => {
  const use = async (c) => {
    try {
      await resolveJob(j.id, { outcome: "published", post_url: c.url, note: `Chosen from ${j.candidates.length} matching posts.` });
      toast.success("Post linked; comment sync started.");
      onChange();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div data-testid={`lab-job-candidates-${j.id}`} className="mt-2 rounded-md border border-violet-200 bg-violet-50 p-2 space-y-1">
      <p className="text-xs font-semibold text-violet-900">More than one post in the group matches this ad. Open each one, then choose the post this job made:</p>
      {j.candidates.map((c) => (
        <div key={c.post_id} className="flex flex-wrap items-center gap-2 text-xs">
          <a href={c.url} target="_blank" rel="noreferrer" className="underline font-mono" data-testid={`lab-cand-link-${c.post_id}`}>Post {c.post_id}</a>
          <span className="text-stone-600">{c.age_text ? (c.age_text === "just now" ? "just now" : `${c.age_text} ago`) : "time unknown"}{c.photos ? ` · ${c.photos} photos` : ""}{c.has_video ? " · video" : ""}</span>
          <Button size="sm" variant="outline" className="h-6 px-2" data-testid={`lab-cand-use-${c.post_id}`} onClick={() => use(c)}>Use this post</Button>
        </div>
      ))}
    </div>
  );
};

export const JobStatus = ({ s, testId }) => <span data-testid={testId} className={`px-2 py-0.5 rounded-full text-xs font-bold ${TONE[s] || "bg-stone-100 text-stone-700"}`}>{LABEL[s] || s}</span>;

function Resolve({ j, onChange }) {
  const [url, setUrl] = useState("");
  const go = async (outcome) => {
    try {
      const r = await resolveJob(j.id, { outcome, post_url: url || null });
      if (r.pending) toast.info(`${r.message} It's linked only if Facebook shows it's a post in your Primary Facebook Group.`); else toast.success("Resolved");
      onChange();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div data-testid={`lab-job-resolve-${j.id}`} className="mt-2 grid sm:grid-cols-[1fr_auto_auto] gap-2">
      <Input data-testid={`lab-job-resolve-url-${j.id}`} placeholder="Paste the post link (group link or Copy Link /share/p/…) if it was posted" value={url} onChange={(e) => setUrl(e.target.value)} />
      <Button data-testid={`lab-job-resolve-posted-${j.id}`} size="sm" disabled={!url} onClick={() => go("published")}>It was posted</Button>
      <Button data-testid={`lab-job-resolve-not-${j.id}`} size="sm" variant="outline" onClick={() => go("not_posted")}>I checked — not posted</Button>
    </div>
  );
}

export function JobsList({ jobs, onChange }) {
  const cancel = async (id) => { try { await cancelJob(id); onChange(); } catch (e) { toast.error(errText(e)); } };
  if (!jobs.length) return <p className="text-sm text-stone-500">No publish jobs yet.</p>;
  return (
    <div data-testid="lab-jobs" className="space-y-2">
      {jobs.map((j) => (
        <details key={j.id} data-testid={`lab-job-${j.id}`} className="border rounded-lg p-2 text-sm" open={["uncertain", "awaiting_review", "pending_approval"].includes(j.status)}>
          <summary className="cursor-pointer flex flex-wrap items-center gap-2">
            <b>{j.horse_name}</b> → {j.kind === "resolve" ? "Share link check" : j.kind === "sync" ? "Comment sync" : j.kind === "find" ? "Approved-post check" : `Primary group · ${j.mode === "review" ? "Review" : "Automatic"}`} <JobStatus s={j.status} testId={`lab-job-status-${j.id}`} />
            <span className="text-xs text-stone-500">{fmtMT(j.created_at)}</span>
          </summary>
          {j.error && <p data-testid={`lab-job-error-${j.id}`} className="text-red-700 mt-1">{j.error}</p>}
          {j.result?.post_url && <a className="text-[#1877F2] hover:underline break-all" href={j.result.post_url} target="_blank" rel="noreferrer">{j.result.post_url}</a>}
          {j.last_step && <p data-testid={`lab-job-last-step-${j.id}`} className="text-xs mt-1"><b>Last step reached:</b> {j.last_step.label || j.last_step.step} ({fmtMT(j.last_step.at)})</p>}
          <ol className="mt-1 text-xs text-stone-600 space-y-0.5">{(j.events || []).map((e, i) => <Event key={i} e={e} />)}</ol>
          {["queued", "claimed", "preparing", "uploading"].includes(j.status) && !j.submit_clicked_at && <Button data-testid={`lab-job-cancel-${j.id}`} size="sm" variant="outline" className="mt-2" onClick={() => cancel(j.id)}>Cancel</Button>}
          {j.status === "pending_approval" && <p data-testid={`lab-job-pending-${j.id}`} className="text-xs mt-1 text-violet-900">{j.needs_confirmation ? "Several posts match this ad, so none was linked automatically." : j.find_stopped ? "Stopped looking automatically. Paste the post link below once it's approved." : `If Facebook is holding it for approval, approve it there. The extension checks the group every 5 minutes and saves the link as soon as the post appears (checked ${j.find_attempts || 0} times).`}</p>}
          {j.status === "pending_approval" && j.needs_confirmation && j.candidates?.length > 0 && <Candidates j={j} onChange={onChange} />}
          {["uncertain", "awaiting_review", "pending_approval"].includes(j.status) && <Resolve j={j} onChange={onChange} />}
        </details>
      ))}
    </div>
  );
}
